import { memo, useMemo } from 'react';
import { Polyline } from 'react-leaflet';
import { Marker } from '@adamscybot/react-leaflet-component-marker';
import { AircraftIcon, CarGroundIcon, CustomArrow } from '@icons';
import { RoutePolylineType } from '@lp/features/RoutePlanner/types';
import { getIconMapColor } from '@lp/features/RoutePlanner/utils';
import { GROUND_LEG_TYPES, REPOSITION_SEGMENT_TYPES } from '@/utils/route';

type GetMiddleIconProps = {
  isGround: boolean;
  isReposition: boolean;
  direction: 'left' | 'right';
  color: string;
  index: string;
};

const getMiddleIcon = ({ isGround, direction, isReposition, color, index }: GetMiddleIconProps) => {
  const TransportIcon = isGround ? CarGroundIcon : AircraftIcon;

  return (
    <div className="flex gap-0.5 items-center py-1 px-1.5 rounded-full text-white" style={{ backgroundColor: color }}>
      <CustomArrow
        width={14}
        height={14}
        style={{
          transform: `rotate(${direction === 'left' ? '180deg' : '0deg'})`,
          order: direction === 'left' ? 1 : 5,
        }}
      />
      {!!index && (
        <span
          className={`text-xs-m text-white px-1 py-0.5 flex justify-center items-center bg-black rounded-full ${direction !== 'left' ? 'order-1' : 'order-6'}`}
        >
          {index}
        </span>
      )}
      <TransportIcon
        width={16}
        height={16}
        style={{ transform: `rotate(${direction === 'left' && !isGround ? '180deg' : '0deg'})`, order: 3 }}
      />
      {isReposition && (
        <span className="text-s-r text-white" style={{ order: direction === 'left' ? 2 : 4 }}>
          REP
        </span>
      )}
    </div>
  );
};

export const RoutePolyline = memo(
  ({ type, legType, positions, middlePosition, direction, index, zoomLevel }: RoutePolylineType) => {
    const isGround = GROUND_LEG_TYPES.includes(legType);
    const isReposition = REPOSITION_SEGMENT_TYPES.includes(type);
    const color = getIconMapColor(type);
    const dashArray = isReposition ? [9, 5] : [0.1, 9];
    const icon = useMemo(
      () => getMiddleIcon({ isGround, color, isReposition, direction, index }),
      [isGround, color, isReposition, direction, index],
    );
    const airClassName = isReposition ? 'leaflet-air-reposition-size' : 'leaflet-air-circle-size';
    const className = isGround ? 'leaflet-ground-stroke' : airClassName;
    const isIconShow = !isGround || (zoomLevel && zoomLevel > 9);

    return (
      <>
        <Polyline
          positions={positions}
          className={className}
          stroke
          color={color}
          dashArray={isGround ? [0, 0] : dashArray}
        />
        {isIconShow && icon && middlePosition ? <Marker position={middlePosition} icon={icon} /> : null}
      </>
    );
  },
);
